// ── C.4b — Captura de pedidos de Comedor: reglas de cliente (sin React) ───────────
// Cada pedido de la captura es una «celda» (empleado × día) con un id_pedido ESTABLE: se crea al elegir el platillo y
// se conserva mientras no cambien empleado, fecha ni platillo, de modo que un reintento (error de red, resultado
// incierto o doble clic) envía exactamente los mismos IDs y el backend (V101) lo reconoce sin duplicar.
// El borrador (celdas + IDs) vive en sessionStorage, separado por usuario, y se borra al completar o al cerrar sesión.

export const CLIENTE_C4B = 'c4b';
const PREFIJO_BORRADOR = 'sigrh.comedor.borrador.v1:';

export function nuevoIdPedido() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Respaldo (navegadores sin randomUUID): UUID v4 con getRandomValues.
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);   // último recurso
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// ── Estados de una celda ──────────────────────────────────────────────────────
// pendiente: lista para enviar (nunca enviada, o el backend dijo que no se guardó).
// enviando:  solicitud en curso.
// incierto:  error de red o escritura sin confirmar: se reenvía con los MISMOS IDs o se verifica; no se edita.
// guardado / ya_existia: confirmado en el backend.
// error:     rechazada por una regla (no se guardó): se puede corregir.
// revision:  FILA_INCOMPLETA / REVISION_REQUERIDA / ID no canónico: requiere revisión humana, nunca se reintenta sola.
export const BLOQUEADAS = ['enviando', 'incierto', 'revision', 'guardado', 'ya_existia'];
export const FINALES = ['guardado', 'ya_existia'];
const CODIGOS_REVISION = ['FILA_INCOMPLETA', 'REVISION_REQUERIDA'];

export const celdaEditable = celda => !celda || !BLOQUEADAS.includes(celda.estado);

// Fija (o cambia) el platillo de una celda. Si cambia empleado/fecha/platillo se genera un id nuevo; si no, se conserva.
// Una celda bloqueada no cambia (devuelve la misma).
export function fijarPlatilloCelda(celda, datos, generarId = nuevoIdPedido) {
  if (celda && !celdaEditable(celda)) return celda;
  const mismo = celda && celda.id_empleado === datos.id_empleado && celda.fecha === datos.fecha && celda.id_platillo === datos.id_platillo;
  if (mismo) return celda;
  return { ...datos, id_pedido: generarId(), estado: 'pendiente', codigo: null, mensaje: null };
}

export const enviables = celdas => celdas.filter(c => c.estado === 'pendiente' || c.estado === 'incierto');

// ── C.5a-2: turno del pedido ──────────────────────────────────────────────────
// Nombres exactos (se guardan e imprimen así; DIA sin acento). MIXTO es un turno, no dos comidas. El turno NO forma parte
// de la identidad del pedido: cambiarlo antes de guardar conserva el mismo id_pedido.
export const TURNOS = ['DIA', 'TARDE', 'NOCHE', 'MIXTO'];
// Turno de una celda editable; la celda bloqueada (enviando, incierta, guardada…) no cambia. propio = elegido para ese
// día, distinto del turno del renglón.
export function fijarTurnoCelda(celda, turno, propio) {
  if (!celda || !celdaEditable(celda)) return celda;
  return { ...celda, turno, turnoPropio: !!propio };
}
// Al cambiar el turno del renglón: sólo las celdas editables que no tienen un turno propio de ese día.
export const aplicarTurnoFila = (celdas, turno) => Object.fromEntries(Object.entries(celdas || {}).map(([k, c]) =>
  [k, celdaEditable(c) && !c.turnoPropio ? { ...c, turno } : c]));
// Celdas por enviar que no tienen turno: no se envían hasta elegirlo.
export const sinTurno = celdas => enviables(celdas).filter(c => !TURNOS.includes(c.turno));

// Cuerpo de crear-bulk: siempre con id_pedido, turno y la marca de cliente. Nombre, sucursal y precio no se envían:
// el backend los toma de las hojas.
export const solicitudLote = celdas => ({
  cliente: CLIENTE_C4B,
  pedidos: celdas.map(c => ({ id_pedido: c.id_pedido, id_empleado: c.id_empleado, fecha_entrega: c.fecha, id_platillo: c.id_platillo, turno: c.turno, cliente: CLIENTE_C4B }))
});

// Interpreta la respuesta de crear-bulk (o su ausencia) para las celdas enviadas, en el mismo orden.
// data === null: error de red o respuesta ilegible → todas «incierto», con sus IDs intactos.
export function aplicarRespuestaLote(enviadas, data) {
  if (!data || typeof data !== 'object') {
    return enviadas.map(c => ({ ...c, estado: 'incierto', codigo: null, mensaje: 'Sin respuesta del servidor: verifica o reintenta con los mismos IDs.' }));
  }
  if (!Array.isArray(data.resultados) || data.resultados.length !== enviadas.length) {
    // Sin detalle por pedido: sólo hay duda si la escritura pudo haber ocurrido.
    const dudosa = data.estado_escritura && data.estado_escritura !== 'NINGUNO';
    const sinEscribir = data.estado_escritura === 'NINGUNO';
    return enviadas.map(c => ({ ...c, estado: dudosa ? 'incierto' : sinEscribir ? 'pendiente' : 'error', codigo: data.codigo || data.estado_escritura || null,
      mensaje: data.error || 'Respuesta incompleta del servidor.' }));
  }
  return enviadas.map((c, i) => {
    const r = data.resultados[i] || {};
    const id = r.id_pedido || c.id_pedido;
    if (r.success) return { ...c, id_pedido: id, estado: r.ya_existia ? 'ya_existia' : 'guardado', codigo: null, mensaje: r.ya_existia ? 'Ya estaba registrado' : null };
    if (CODIGOS_REVISION.includes(r.codigo)) return { ...c, estado: 'revision', codigo: r.codigo, mensaje: r.error };
    if (r.codigo === 'NO_GUARDADO_POR_LOTE' || r.codigo === 'NO_PROCESADO_POR_LOTE') return { ...c, estado: 'pendiente', codigo: r.codigo, mensaje: r.error };
    if (r.estado_guardado === 'INCOMPLETO' || r.estado_guardado === 'NO_CANONICO') return { ...c, estado: 'revision', codigo: r.estado_guardado, mensaje: r.error };
    if (r.estado_guardado === 'NO_GUARDADO') return { ...c, estado: 'pendiente', codigo: 'NO_GUARDADO', mensaje: r.error };
    if (data.estado_escritura === 'NINGUNO') return { ...c, estado: 'pendiente', codigo: 'NINGUNO', mensaje: r.error || data.error };
    if (data.estado_escritura) return { ...c, estado: 'incierto', codigo: r.estado_guardado || data.estado_escritura, mensaje: r.error || data.error };
    return { ...c, estado: 'error', codigo: r.codigo || null, mensaje: r.error || data.error || 'No se guardó' };
  });
}

// Reconciliación con lo guardado (pedidos-semana del backend, que sólo entrega pedidos completos): para cada celda
// incierta o pendiente se decide qué hay realmente. Se llama al restaurar un borrador y antes de permitir editar una
// celda incierta. No inventa nada: si no lo encuentra, la celda queda «pendiente» con su MISMO id.
const fechaDe = p => String(p.fecha_entrega).slice(0, 10);
const empDe = v => { const t = String(v === undefined || v === null ? '' : v).trim(); return /^\d{1,5}$/.test(t) ? t.padStart(5, '0') : t; };
export function reconciliarCeldas(celdas, pedidosGuardados) {
  const guardados = pedidosGuardados || [];
  return celdas.map(c => {
    if (!['incierto', 'pendiente', 'enviando'].includes(c.estado)) return c;
    const porId = guardados.find(p => p.id_pedido === c.id_pedido);
    if (porId) {
      const coincide = empDe(porId.id_empleado) === empDe(c.id_empleado) && fechaDe(porId) === c.fecha && String(porId.id_platillo) === String(c.id_platillo);
      if (!coincide) return { ...c, estado: 'revision', codigo: 'ID_PEDIDO_REUTILIZADO', mensaje: 'Ese ID ya pertenece a otro pedido: revisa.' };
      if (porId.estado === 'activo') return { ...c, estado: 'guardado', codigo: null, mensaje: 'Confirmado: quedó guardado' };
      return { ...c, estado: 'revision', codigo: 'ID_PEDIDO_CANCELADO', mensaje: 'Ese pedido existe y está cancelado: revisa.' };
    }
    const activoDelDia = guardados.find(p => p.estado === 'activo' && empDe(p.id_empleado) === empDe(c.id_empleado) && fechaDe(p) === c.fecha);
    if (activoDelDia && String(activoDelDia.id_platillo) === String(c.id_platillo)) {
      return { ...c, id_pedido: activoDelDia.id_pedido, estado: 'ya_existia', codigo: null, mensaje: 'Ya estaba registrado' };
    }
    if (activoDelDia) return { ...c, estado: 'error', codigo: 'DUPLICADO_EMPLEADO_DIA', mensaje: 'Ya tiene otro pedido activo ese día (' + (activoDelDia.nombre_platillo || activoDelDia.id_platillo) + ').' };
    return { ...c, estado: 'pendiente', codigo: null, mensaje: c.estado === 'incierto' ? 'Verificado: no quedó guardado; se reenviará con el mismo ID.' : c.mensaje };
  });
}

// ── Cambiar ───────────────────────────────────────────────────────────────────
// El cambio guarda sus dos IDs; un reintento usa los mismos. CAMBIO_INCOMPLETO o sin respuesta → reintento permitido;
// REVISION_REQUERIDA / FILA_INCOMPLETA → sólo revisión.
export const solicitudCambio = cambio => ({ id_pedido_anterior: cambio.id_pedido_anterior, id_pedido_nuevo: cambio.id_pedido_nuevo, id_platillo: cambio.id_platillo, turno: cambio.turno, cliente: CLIENTE_C4B });
export function aplicarRespuestaCambio(cambio, data) {
  if (!data || typeof data !== 'object') return { ...cambio, estado: 'incierto', mensaje: 'Sin respuesta del servidor: reintenta con los mismos IDs.' };
  if (data.success) return { ...cambio, estado: 'hecho', mensaje: data.aviso === 'BITACORA_PENDIENTE' ? 'Cambio hecho (la bitácora quedó pendiente).' : null };
  if (CODIGOS_REVISION.includes(data.codigo) || data.codigo === 'ID_PEDIDO_REUTILIZADO') return { ...cambio, estado: 'revision', codigo: data.codigo, mensaje: data.error };
  if (data.codigo === 'CAMBIO_INCOMPLETO' || !data.codigo) return { ...cambio, estado: 'reintentable', codigo: data.codigo || null, mensaje: data.error };
  return { ...cambio, estado: 'error', codigo: data.codigo, mensaje: data.error };
}
export function reconciliarCambio(cambio, pedidosGuardados) {
  const g = pedidosGuardados || [];
  const nuevo = g.find(p => p.id_pedido === cambio.id_pedido_nuevo), anterior = g.find(p => p.id_pedido === cambio.id_pedido_anterior);
  if (nuevo && nuevo.estado === 'activo' && anterior && anterior.estado === 'cancelado') return { ...cambio, estado: 'hecho', mensaje: 'Confirmado: el cambio quedó hecho.' };
  if (nuevo) return { ...cambio, estado: 'reintentable', mensaje: 'El cambio quedó a medias: reintenta para terminarlo.' };
  return { ...cambio, estado: 'reintentable', mensaje: 'El cambio no se hizo: puedes reintentarlo o descartarlo.' };
}

// ── Borrador en sessionStorage (por usuario) ─────────────────────────────────
const almacen = () => { try { return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null; } catch (e) { return null; } };
export function leerBorrador(email) {
  const s = almacen(); if (!s || !email) return null;
  try { const v = JSON.parse(s.getItem(PREFIJO_BORRADOR + email) || 'null'); return v && v.version === 1 ? v : null; } catch (e) { return null; }
}
export function guardarBorrador(email, borrador) {
  const s = almacen(); if (!s || !email) return;
  try { s.setItem(PREFIJO_BORRADOR + email, JSON.stringify({ ...borrador, version: 1, actualizado: new Date().toISOString() })); } catch (e) { /* sin almacenamiento: se trabaja en memoria */ }
}
export function borrarBorrador(email) {
  const s = almacen(); if (!s || !email) return;
  try { s.removeItem(PREFIJO_BORRADOR + email); } catch (e) { /* nada que borrar */ }
}
// El borrador tiene dos partes independientes: { captura, cambio }. Sin ninguna, se borra.
export function actualizarBorrador(email, partes) {
  const n = { ...(leerBorrador(email) || {}), ...partes };
  if (!n.captura && !n.cambio) borrarBorrador(email); else guardarBorrador(email, n);
}
// Al cerrar sesión: borra los borradores de Comedor de cualquier usuario en esta pestaña.
export function borrarBorradoresComedor() {
  const s = almacen(); if (!s) return;
  try { const claves = []; for (let i = 0; i < s.length; i++) { const k = s.key(i); if (k && k.indexOf(PREFIJO_BORRADOR) === 0) claves.push(k); } claves.forEach(k => s.removeItem(k)); } catch (e) { /* nada */ }
}
// Un borrador se puede descartar sin verificar sólo si nada quedó en duda.
export const descartableSinVerificar = celdas => !celdas.some(c => c.estado === 'incierto' || c.estado === 'enviando');
